// AddProductPage – CSV or Manual option, premium UX
import { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import { FiUpload, FiX, FiArrowLeft, FiDownload, FiSave, FiAlertCircle, FiCheckCircle, FiZap, FiLock, FiCheck } from 'react-icons/fi';
import { BsFileSpreadsheet, BsFillKeyboardFill, BsFileEarmarkText, BsClipboardData, BsGrid3X3Gap, BsImages, BsSliders, BsBox } from 'react-icons/bs';
import { BiLoaderAlt } from 'react-icons/bi';
import { MdUploadFile, MdBarChart, MdAttachMoney, MdInventory } from 'react-icons/md';
import toast from 'react-hot-toast';
import * as XLSX from 'xlsx';
import ExcelJS from 'exceljs';
import Swal from 'sweetalert2';
import { productAPI } from '../../api';
import './Product.css';


/* ── helper components for Manual Form ── */
const Section = ({ title, children }: any) => (
  <div className="csv-card" style={{ padding: '24px', marginBottom: '20px' }}>
    <h3 style={{ fontWeight: '700', fontSize: '15px', color: '#0f172a', marginBottom: '18px', paddingBottom: '12px', borderBottom: '1px solid #f1f5f9' }}>{title}</h3>
    <div style={{ display: 'grid', gap: '16px', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))' }}>
      {children}
    </div>
  </div>
);

const Field = ({ label, name, value, onChange, type = 'text', placeholder, required, span, options, ...rest }: any) => (
  <div style={span ? { gridColumn: 'span 2' } : {}} className="manual-field">
    <label className="manual-field-label">{label} {required && <span className="req">*</span>}</label>
    {type === 'textarea' ? (
      <textarea name={name} value={value} onChange={onChange}
        placeholder={placeholder} rows={3} style={{ resize: 'vertical' }} {...rest} />
    ) : type === 'select' ? (
      <select
        name={name}
        value={value}
        onChange={onChange}
        {...rest}
      >
        {options && options.map((o: any) => {
          const optionValue = typeof o === 'string' ? o : o.value;
          const optionLabel = typeof o === 'string' ? o : o.label;
          return <option key={optionValue} value={optionValue}>{optionLabel}</option>;
        })}
      </select>
    ) : type === 'checkbox-group' ? (
      <div style={{ display: 'flex', gap: '16px', marginTop: '8px' }}>
        {options.map((o: any) => (
          <label key={o} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '14px', color: '#334155' }}>
            <input type="checkbox" name={name} value={o} checked={value.includes(o)}
              onChange={(e) => {
                const checked = e.target.checked;
                const newValue = checked ? [...value, o] : value.filter((v: any) => v !== o);
                onChange({ target: { name, value: newValue } });
              }} style={{ cursor: 'pointer', width: '16px', height: '16px', accentColor: '#6366f1' }} />
            {o}
          </label>
        ))}
      </div>
    ) : (
      <input type={type} name={name} value={value}
        onChange={onChange} placeholder={placeholder} {...rest} />
    )}
  </div>
);

/* ── COMPONENT: Manual Add Panel ── */
function ManualAddPanel({ onCancel }: { onCancel: () => void }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [adminProducts, setAdminProducts] = useState<any[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const userDetails = JSON.parse(localStorage.getItem('seller') || '{}');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const [form, setForm] = useState({
    product_parent_id: '',
    sku: '',
    price: '', // MRP
    sellingPrice: '',
    discount: '',
    tax: '',
    hsn: '',
    gstAmount: '',
    netAmount: '',
    stock: '',
    warranty: '',
    replacementDays: '7',
    expiryDate: '',
    status: 'A',
    paymentOptions: ['Online'],
    latitude: '',
    longitude: '',
  });

  // Removed tax fetch config

  // Reset pagination when search changes
  useEffect(() => {
    setPage(1);
    setAdminProducts([]);
    setHasMore(true);
  }, [searchTerm]);

  useEffect(() => {
    const fetchAdminProducts = async () => {
      if (!hasMore && page > 1) return;
      setLoadingMore(true);
      try {
        const res = await productAPI.getAdminProductsDropdown({ page, limit: 20, search: searchTerm });
        if (res.data.response === 'S') {
          const newData = res.data.data;
          setAdminProducts(prev => page === 1 ? newData : [...prev, ...newData]);
          setHasMore(newData.length === 20);
        }
      } catch (err) {
        toast.error('Failed to load parent products list');
        console.error(err);
      } finally {
        setLoadingMore(false);
      }
    };

    const delay = setTimeout(fetchAdminProducts, 300);
    return () => clearTimeout(delay);
  }, [page, searchTerm]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.manual-dropdown-wrapper')) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleDropdownScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop <= clientHeight + 50 && !loadingMore && hasMore) {
      setLoadingMore(true);
      setPage(prev => prev + 1);
    }
  };

  const handleSelectProduct = (prod: any) => {
    setSelectedProduct(prod);
    setForm(prev => ({ ...prev, product_parent_id: prod._id, sku: prod.sku || '' }));
    setDropdownOpen(false);
    setSearchTerm('');
    setErrors(prev => ({ ...prev, product_parent_id: '', sku: '' }));
  };

  const handleClearProduct = () => {
    setSelectedProduct(null);
    setForm(prev => ({ ...prev, product_parent_id: '', sku: '' }));
  };

  const handleBlur = (e: any) => {
    const { name, value } = e.target;
    if (['price', 'sellingPrice'].includes(name) && value) {
      const parsed = parseFloat(Number(value).toFixed(2));
      setForm(prev => {
        const updated = { ...prev, [name]: String(parsed) };
        if (name === 'price') {
          const disc = Number(updated.discount) || 0;
          updated.sellingPrice = parsed > 0 ? String(parseFloat(Math.max(0, parsed - (parsed * disc / 100)).toFixed(2))) : '';
        } else if (name === 'sellingPrice') {
          const mrp = Number(updated.price) || 0;
          if (mrp > 0 && parsed <= mrp) {
            const calculatedDisc = ((mrp - parsed) / mrp) * 100;
            updated.discount = String(parseFloat(calculatedDisc.toFixed(2)));
          }
        }
        return updated;
      });
    }
  };

  const handleChange = (e: any) => {
    let { name, value } = e.target;

    // Restrict input to numbers only
    if (['price', 'sellingPrice', 'discount', 'tax'].includes(name)) {
      value = value.replace(/[^0-9.]/g, '');
      if ((value.match(/\./g) || []).length > 1) return; // Prevent multiple dots
    } else if (name === 'hsn') {
      value = value.replace(/[^0-9]/g, '');
      if (value.length > 10) return;
    } else if (['latitude', 'longitude'].includes(name)) {
      value = value.replace(/[^0-9.-]/g, '');
      if ((value.match(/\./g) || []).length > 1) return;
      if ((value.match(/-/g) || []).length > 1) return;
    } else if (['stock', 'warranty', 'replacementDays'].includes(name)) {
      value = value.replace(/[^0-9]/g, '');
    }

    // Validation for max values
    if (name === 'discount' && value !== '' && value !== '.') {
      if (Number(value) > 100) return;
    }
    if (name === 'tax' && value !== '' && value !== '.') {
      if (Number(value) > 100) return;
    }
    if (name === 'warranty' && value !== '') {
      if (Number(value) > 150) return;
    }

    setForm(prev => {
      const updated = { ...prev, [name]: value };

      // Dynamic calculations
      const mrp = Number(updated.price) || 0;

      if (name === 'price') {
        const disc = Number(updated.discount) || 0;
        const calculatedSp = mrp > 0 ? parseFloat(Math.max(0, mrp - (mrp * disc / 100)).toFixed(2)) : 0;
        updated.sellingPrice = calculatedSp > 0 ? String(calculatedSp) : '';
      } else if (name === 'discount') {
        const disc = Number(value) || 0;
        const calculatedSp = mrp > 0 ? parseFloat(Math.max(0, mrp - (mrp * disc / 100)).toFixed(2)) : 0;
        updated.sellingPrice = calculatedSp > 0 ? String(calculatedSp) : '';
      } else if (name === 'sellingPrice') {
        const sp = Number(value) || 0;
        if (mrp > 0 && sp <= mrp) {
          const calculatedDisc = ((mrp - sp) / mrp) * 100;
          updated.discount = String(parseFloat(calculatedDisc.toFixed(2)));
        } else if (mrp > 0 && sp > mrp) {
          updated.discount = '0';
        }
      }

      if (['price', 'discount', 'sellingPrice', 'tax'].includes(name)) {
        const spToUse = Number(updated.sellingPrice) || 0;
        const taxToUse = Number(updated.tax) || 0;
        if (spToUse > 0 && updated.tax !== '') {
          const gstVal = parseFloat((spToUse * taxToUse / 100).toFixed(2));
          updated.gstAmount = String(gstVal);
          updated.netAmount = String(parseFloat((spToUse + gstVal).toFixed(2)));
        } else {
          updated.gstAmount = '';
          updated.netAmount = '';
        }
      }

      return updated;
    });

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    const newErrors: { [key: string]: string } = {};
    if (!form.product_parent_id) newErrors.product_parent_id = 'Fill the Product Selection';
    if (form.price === '') newErrors.price = 'Fill the MRP';
    if (form.sellingPrice === '') newErrors.sellingPrice = 'Fill the Selling Price';
    if (form.stock === '') newErrors.stock = 'Fill the Stock Quantity';
    if (form.paymentOptions.length === 0) newErrors.paymentOptions = 'Choose payment options';

    if (form.hsn.length > 10 || form.hsn.length < 4) {
      newErrors.hsn = 'HSN must be between 4 and 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      Swal.fire({
        icon: 'error',
        title: 'Validation Error',
        html: `<div style="text-align: left; font-size: 14px;">
                 <ul style="margin: 0; padding-left: 20px;">
                   ${Object.values(newErrors).map(err => `<li>${err}</li>`).join('')}
                 </ul>
               </div>`
      });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append('product_parent_id', form.product_parent_id);
      fd.append('sku', form.sku);
      fd.append('mrp', String(Math.min(10000000, Math.max(0, Number(form.price) || 0))));
      fd.append('price', String(Math.min(10000000, Math.max(0, Number(form.sellingPrice) || 0))));
      fd.append('discount', String(Math.min(100, Math.max(0, Number(form.discount) || 0))));
      if (form.tax !== '') {
        fd.append('tax', form.tax);
      }
      if (form.hsn !== '') {
        fd.append('hsn', form.hsn);
      }
      if (form.gstAmount !== '') {
        fd.append('gstAmount', form.gstAmount);
      }
      if (form.netAmount !== '') {
        fd.append('netAmount', form.netAmount);
      }
      fd.append('stock', String(Math.max(0, Math.floor(Number(form.stock) || 0))));
      fd.append('warranty', form.warranty);
      fd.append('replacementDays', form.replacementDays);
      if (form.expiryDate !== undefined) fd.append('expiryDate', form.expiryDate);
      fd.append('status', form.status);
      fd.append('paymentOptions', JSON.stringify(form.paymentOptions));
      if (form.latitude !== '') fd.append('latitude', form.latitude);
      if (form.longitude !== '') fd.append('longitude', form.longitude);
      await productAPI.create(fd);
      toast.success('Product added successfully!');
      navigate('/products');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to add product');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="manual-form-container">
      {/* ── LEFT COLUMN: Form Fields ── */}
      <div className="manual-form-main">
        {/* Product Selection */}
        <div className="manual-card" style={{ zIndex: 10 }}>
          <div className="manual-card-title">
            <BsGrid3X3Gap size={14} color="#6366f1" /> Product Selection
          </div>

          <div className="manual-dropdown-wrapper">
            <div
              className={`manual-dropdown-trigger ${dropdownOpen ? 'open' : ''}`}
              onClick={() => setDropdownOpen(!dropdownOpen)}
            >
              {selectedProduct ? (
                <span className="manual-dropdown-chip">
                  {selectedProduct.name}
                  {selectedProduct.sku ? ` · ${selectedProduct.sku}` : ''}
                  <span className="chip-x" onClick={(e) => { e.stopPropagation(); handleClearProduct(); }}>✕</span>
                </span>
              ) : (
                <span style={{ fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>
                  Search & select a product…
                </span>
              )}
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}>
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>

            {dropdownOpen && (
              <div className="manual-dropdown-menu">
                <input
                  className="manual-dropdown-search"
                  type="text"
                  placeholder="Type to search products…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  autoFocus
                />
                <div className="manual-dropdown-list" onScroll={handleDropdownScroll}>
                  {adminProducts.length > 0 ? (
                    <>
                      {adminProducts.map((p: any) => (
                        <div
                          key={p._id}
                          className={`manual-dropdown-item ${form.product_parent_id === p._id ? 'selected' : ''}`}
                          onClick={() => handleSelectProduct(p)}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div style={{
                              width: '32px', height: '32px', borderRadius: '6px',
                              background: '#f1f5f9', display: 'flex', alignItems: 'center',
                              justifyContent: 'center', overflow: 'hidden', flexShrink: 0,
                              border: '1px solid #e2e8f0'
                            }}>
                              {p.images && p.images.length > 0 ? (
                                <img src={p.images[0]?.url || p.images[0]} alt={p.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <BsBox size={16} color="#94a3b8" />
                              )}
                            </div>
                            <span className="manual-dropdown-item-name" style={{ maxWidth: '200px' }}>{p.name}</span>
                          </div>
                          {p.sku && <span className="manual-dropdown-item-sku">{p.sku}</span>}
                        </div>
                      ))}
                      {loadingMore && <div style={{ padding: '10px', textAlign: 'center', fontSize: '12px', color: '#94a3b8' }}>Loading more...</div>}
                    </>
                  ) : (
                    <div className="manual-dropdown-empty">No products found</div>
                  )}
                </div>
              </div>
            )}
            {errors.product_parent_id && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.product_parent_id}</div>}
          </div>
        </div>

        {/* Pricing & SKU */}
        <div className="manual-card">
          <div className="manual-card-title">
            <MdAttachMoney size={15} color="#f59e0b" /> Pricing & Reseller SKU
          </div>
          <div className="manual-fields-grid">
            <div className="manual-field">
              <label className="manual-field-label">
                Reseller SKU
              </label>

              <input
                name="sku"
                value={form.sku}
                onChange={handleChange}
                placeholder="e.g. DS-2026-XYZ"
                disabled
                style={{ backgroundColor: '#e1e6e4ff', cursor: 'not-allowed' }}
              />
              {errors.sku && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.sku}</div>}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">
                MRP (₹) <span className="req">*</span>
              </label>

              <input
                name="price"
                type="number"
                value={form.price}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="0.00"
                step="any"
                min={1}
                max={10000000}
              />
              {errors.price && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.price}</div>}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">
                Selling Price (₹) <span className="req">*</span><div style={{ color: '#64748b', fontSize: '11px', fontStyle: 'italic', marginTop: '4px' }}>
                (Inclusive of all taxes)
              </div>
              </label>
                
              <input
                name="sellingPrice"
                type="number"
                value={form.sellingPrice}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="0.00"
                step="any"
                min={1}
                max={10000000}
              />

              {errors.sellingPrice && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.sellingPrice}</div>}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">Discount Percent (%)</label>
              <input
                name="discount"
                type="number"
                value={form.discount}
                onChange={handleChange}
                placeholder="0"
              />
              {errors.discount && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.discount}</div>}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">GST Percent (%)</label>
              <select
                name="tax"
                value={form.tax}
                onChange={handleChange}
              >
                <option value="" disabled>Select GST %</option>
                <option value="0">0%</option>
                <option value="0.25">0.25%</option>
                <option value="3">3%</option>
                <option value="5">5%</option>
                <option value="12">12%</option>
                <option value="18">18%</option>
                <option value="28">28%</option>
              </select>
            </div>
              {form.sellingPrice && form.tax && (
                <div className="manual-field">
                  <label className="manual-field-label">GST Amount (₹)</label>
                  <input
                    name="gstAmount"
                    type="text"
                    value={form.gstAmount}
                    disabled
                    style={{ background: '#f8fafc', color: '#64748b' }}
                  />
                </div>
              )}
            
            <div className="manual-field">
              <label className="manual-field-label">HSN Number</label>
              <input
                name="hsn"
                type="number"
                maxLength={10}
                minLength={4}
                value={form.hsn}
                onChange={handleChange}
                placeholder="Enter HSN number (digits between 4 and 10)"
              />
              {errors.hsn && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.hsn}</div>}
            </div>

            {form.sellingPrice && form.tax && (
              <div className="manual-field">
                <label className="manual-field-label">Net Amount (₹)</label>
                <input
                  name="netAmount"
                  type="text"
                  value={form.netAmount}
                  disabled
                  style={{ background: '#f8fafc', color: '#64748b' }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Inventory & Status */}
        <div className="manual-card">
          <div className="manual-card-title">
            <MdInventory size={14} color="#10b981" /> Inventory
          </div>
          <div className="manual-fields-grid">
            {/* <div className="manual-field">
              <label className="manual-field-label">Stock Quantity <span className="req">*</span></label>
              <input name="stock" type="number" value={form.stock} onChange={handleChange} placeholder="0" />
              {errors.stock && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.stock}</div>}
            </div> */}
            <div className="manual-field">
              <label className="manual-field-label">
                Stock Quantity <span className="req">*</span>
              </label>

              <input
                name="stock"
                type="number"
                min="0"
                value={form.stock}
                onChange={handleChange}
                placeholder="0"
              />

              {errors.stock && (
                <div
                  style={{
                    color: '#ef4444',
                    fontSize: '12px',
                    marginTop: '6px',
                    fontWeight: '500'
                  }}
                >
                  {errors.stock}
                </div>
              )}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">Warranty (Months) e.g., 16 to 1year 4months</label>
              <input
                name="warranty"
                type="number"
                min={0}
                max={150}
                value={form.warranty}
                onChange={handleChange}
                onKeyDown={(e) => {
                  // Prevent decimal, exponent, + and -
                  if (['.', 'e', 'E', '+', '-'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                placeholder="Enter warranty duration (max 150 months)"
              />
              {errors.warranty && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.warranty}</div>}
            </div>
            <div className="manual-field">
              <label className="manual-field-label">Replacement Days</label>
              <select name="replacementDays" value={form.replacementDays} onChange={handleChange} style={{ width: '100%', padding: '11px 16px', borderRadius: '8px', border: '1px solid #e2e8f0', backgroundColor: '#ffffff', color: '#0f172a', fontSize: '14px', fontWeight: '500', outline: 'none' }}>
                <option value="0">0 (No Replacement)</option>
                <option value="7">7 Days</option>
                <option value="10">10 Days</option>
                <option value="15">15 Days</option>
                <option value="30">30 Days</option>
              </select>
            </div>
            {/* <div className="manual-field">
              <label className="manual-field-label">Status</label>
              <select name="status" value={form.status} onChange={handleChange}>
                <option value="A">Active</option>
                <option value="D">Draft</option>
              </select>
            </div> */}
            <div className="manual-field">
              <label className="manual-field-label">
                Expiry Date <span style={{ color: '#94a3b8', fontWeight: 'normal', fontSize: '11px', marginLeft: '4px' }}>(Optional)</span>
              </label>
              <input
                name="expiryDate"
                type="date"
                value={form.expiryDate}
                onChange={handleChange}
                min={new Date().toISOString().split("T")[0]}
              />
            </div>
            <div className="manual-field" style={{ gridColumn: '1 / -1' }}>
              <label className="manual-field-label">
                Payment Options
              </label>
              <p style={{fontSize:"12px", color:"#e7890dff"}}>Online payment is selected by default. You can continue with Cash on Delivery without selecting any payment option.</p>
              <div style={{ display: 'flex', gap: '12px', marginTop: '12px', flexWrap: 'wrap' }}>
                {['Online', 'Cash on Delivery'].map((option) => {
                  const isSelected = form.paymentOptions.includes(option);
                  const isOnline = option === 'Online';
                  return (
                    <div
                      key={option}
                      onClick={() => {
                        if (isOnline) return;
                        setForm(prev => {
                          const opts = new Set(prev.paymentOptions);
                          if (isSelected) opts.delete(option);
                          else opts.add(option);
                          return { ...prev, paymentOptions: Array.from(opts) };
                        });
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 16px',
                        borderRadius: '12px',
                        border: `1.5px solid ${isSelected ? '#10b981' : '#e2e8f0'}`,
                        background: isSelected ? (isOnline ? '#e2e8f0' : '#ecfdf5') : '#f8fafc',
                        cursor: isOnline ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isSelected && !isOnline ? '0 4px 12px -2px rgba(16, 185, 129, 0.15)' : 'none',
                        transform: isSelected && !isOnline ? 'translateY(-1px)' : 'none',
                        opacity: isOnline ? 0.7 : 1
                      }}
                    >
                      <div style={{
                        width: '18px',
                        height: '18px',
                        borderRadius: '6px',
                        background: isSelected ? (isOnline ? '#94a3b8' : '#10b981') : 'white',
                        border: `2px solid ${isSelected ? (isOnline ? '#94a3b8' : '#10b981') : '#cbd5e1'}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s'
                      }}>
                        {isSelected && <FiCheck size={12} strokeWidth={4} color="white" />}
                      </div>
                      <span style={{
                        fontSize: '14px',
                        fontWeight: isSelected ? '700' : '500',
                        color: isSelected ? (isOnline ? '#475569' : '#065f46') : '#475569',
                        transition: 'color 0.2s'
                      }}>
                        {option}
                      </span>
                    </div>
                  );
                })}
              </div>
              {errors.paymentOptions && <div style={{ color: '#ef4444', fontSize: '12px', marginTop: '6px', fontWeight: '500' }}>{errors.paymentOptions}</div>}
            </div>
          </div>
        </div>
      

        {/* Actions */}
        <div className="manual-actions">
          <button type="button" className="manual-btn-cancel" onClick={onCancel}>
            <FiX size={15} style={{ display: 'inline-block' }} /> Cancel
          </button>
          <button type="submit" className="manual-btn-save" disabled={loading}>
            {loading ? (
              <BiLoaderAlt size={15} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} />
            ) : (
              <FiSave size={15} style={{ display: 'inline-block' }} />
            )}
            {loading ? 'Saving…' : 'Save Product'}
          </button>
        </div>
      </div>

      {/* ── RIGHT COLUMN: Interactive Live Preview ── */}
      <div className="manual-preview-sidebar">
        {selectedProduct ? (
          <>
            <div className="manual-preview-header">
              <div className="manual-preview-img-box" style={{ overflow: 'hidden', padding: selectedProduct.images && selectedProduct.images.length > 0 ? 0 : undefined }}>
                {selectedProduct.images && selectedProduct.images.length > 0 ? (
                  <img src={selectedProduct.images[0]?.url || selectedProduct.images[0]} alt={selectedProduct.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <BsBox size={32} color="#94a3b8" />
                )}
              </div>
              <div className="manual-preview-name" title={selectedProduct.name}>
                {selectedProduct.name}
              </div>
              <div className="manual-preview-category">
                {selectedProduct.category || 'No Category'}
              </div>
            </div>

            <div className="manual-preview-body">
              <div className="manual-preview-row">
                <span className="label">Brand</span>
                <span className="value">{selectedProduct.brand || '—'}</span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Base SKU</span>
                <span className="value" style={{ fontFamily: 'monospace' }}>{selectedProduct.sku || '—'}</span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Custom SKU</span>
                <span className="value" style={{ fontFamily: 'monospace', color: '#4f46e5' }}>{form.sku || '—'}</span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Stock Available</span>
                <span className="value" style={{ color: form.stock ? '#10b981' : '#94a3b8' }}>
                  {form.stock ? `${form.stock} units` : 'Not specified'}
                </span>
              </div>
              <div className="manual-preview-row">
                <span className="label">Listing Status</span>
                <span className="value">
                  <span style={{
                    padding: '3px 8px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '700',
                    background: form.status === 'A' ? '#d1fae5' : '#fee2e2',
                    color: form.status === 'A' ? '#065f46' : '#991b1b'
                  }}>
                    {form.status === 'A' ? 'ACTIVE' : 'DRAFT'}
                  </span>
                </span>
              </div>

              {/* Dynamic payout widget */}
              <div className="manual-pricing-widget">
                <div className="manual-pricing-widget-header">Pricing Breakdown</div>
                <div className="manual-preview-row" style={{ padding: '6px 0' }}>
                  <span className="label">Retail Price</span>
                  <span className="value">₹{Number(form.price || 0).toFixed(0)}</span>
                </div>
                <div className="manual-preview-row" style={{ padding: '6px 0' }}>
                  <span className="label">Reseller Discount ({form.discount || 0}%)</span>
                  <span className="value" style={{ color: '#dc2626' }}>
                    -₹{(Number(form.price || 0) * Number(form.discount || 0) / 100).toFixed(0)}
                  </span>
                </div>
                <div className="manual-pricing-payout">
                  <span className="title">Net Customer Price</span>
                  <span className="value">
                    ₹{(Number(form.price || 0) * (1 - Number(form.discount || 0) / 100)).toFixed(0)}
                  </span>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="manual-preview-placeholder">
            <BsGrid3X3Gap size={40} />
            <h4 style={{ margin: '14px 0 6px 0', fontSize: '15px', color: '#475569', fontWeight: '700' }}>
              Preview Panel
            </h4>
            <p>Select a parent product to view real-time layout rendering, calculated profit summaries, and list preview.</p>
          </div>
        )}
      </div>
    </form>
  );
}

/* ── COMPONENT: Template Selection Modal ── */
export default ManualAddPanel;
